import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar, Megaphone, Wallet, CloudSun, Pin } from "lucide-react";
import { Link } from "react-router-dom";
import KrakenLoader from "@/components/KrakenLoader";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ announcements: [], events: [], payments: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([
      api.get("/announcements").catch(() => ({ data: [] })),
      api.get("/schedule").catch(() => ({ data: [] })),
      api.get("/payments/mine").catch(() => ({ data: [] })),
    ])
      .then(([a, e, p]) => setData({ announcements: a.data, events: e.data, payments: p.data }))
      .finally(() => setLoading(false));
  }, []);
  const upcoming = [...data.events].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 4);
  const pinned = data.announcements.find((a) => a.pinned) || data.announcements[0];
  const balance = data.payments
    .filter((p) => ["unpaid", "partial", "overdue"].includes(p.status))
    .reduce((s, p) => s + (Number(p.remaining_balance ?? p.amount) || 0), 0);

  return (
    <div data-testid="dashboard-page" className="space-y-5">
      <div>
        <div className="eyebrow mb-1">Welcome back</div>
        <h1 className="font-display text-2xl sm:text-4xl uppercase tracking-tight break-words">{user?.name}</h1>
      </div>
      {loading && <KrakenLoader label="Loading your dashboard…" testId="dashboard-loader" />}
      {!loading && (<>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="kr-card rounded-xl p-4 sm:p-5">
          <Megaphone className="text-[#00E5FF] mb-2" size={20} />
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-400">Announcements</div>
          <div className="font-display text-2xl sm:text-3xl">{data.announcements.length}</div>
        </div>
        <div className="kr-card rounded-xl p-4 sm:p-5">
          <Calendar className="text-[#00E5FF] mb-2" size={20} />
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-400">Upcoming Events</div>
          <div className="font-display text-2xl sm:text-3xl">{upcoming.length}</div>
        </div>
        <div className="kr-card rounded-xl p-4 sm:p-5">
          <Wallet className="text-[#00E5FF] mb-2" size={20} />
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-400">Balance Due</div>
          <div className="font-display text-2xl sm:text-3xl break-all">{data.payments.length === 0 ? "$0" : `$${balance.toFixed(0)}`}</div>
          {data.payments.length === 0 && <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">No balance due</div>}
        </div>
        <div className="kr-card rounded-xl p-4 sm:p-5">
          <CloudSun className="text-[#00E5FF] mb-2" size={20} />
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-400">Practice Status</div>
          <div className="font-display text-lg sm:text-xl text-[#00E5FF]">ON</div>
        </div>
      </div>

      {pinned && (
        <div className="kr-card rounded-xl p-6 border-l-4 border-[#00E5FF]" data-testid="pinned-ann">
          <div className="flex items-center gap-2 mb-2">
            <Pin size={14} className="text-[#00E5FF]" />
            <div className="eyebrow">Pinned Announcement</div>
          </div>
          <div className="font-display text-2xl uppercase">{pinned.title}</div>
          <p className="text-slate-300 mt-2">{pinned.body}</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="kr-card rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow">Upcoming</div>
            <Link to="/portal/calendar" className="text-xs text-[#00E5FF]">View all</Link>
          </div>
          <div className="space-y-3">
            {upcoming.map((e) => (
              <div key={e.id} className="flex items-center gap-3 p-3 rounded-md bg-[#050A15]">
                <div className="text-center w-12">
                  <div className="font-display text-xl text-[#00E5FF]">{new Date(e.date).getDate()}</div>
                  <div className="text-[10px] text-slate-500 uppercase">{new Date(e.date).toLocaleString('en-US', { month: 'short' })}</div>
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm">{e.title}</div>
                  <div className="text-xs text-slate-400">{e.location}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-slate-500">{e.type}</span>
              </div>
            ))}
            {upcoming.length === 0 && <p className="text-slate-500 text-sm">Nothing scheduled.</p>}
          </div>
        </div>
        <div className="kr-card rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow">Quick Links</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/portal/documents" className="btn-ghost text-sm justify-center">Documents</Link>
            <Link to="/portal/payments" className="btn-ghost text-sm justify-center">Payments</Link>
            <Link to="/portal/volunteer" className="btn-ghost text-sm justify-center">Volunteer</Link>
            <Link to="/portal/service" className="btn-ghost text-sm justify-center">Service Log</Link>
          </div>
        </div>
      </div>
      </>)}
    </div>
  );
}
